import { useTheme } from '@emotion/react';
import React, {
    Children,
    ComponentProps,
    CSSProperties,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useScrollDirection } from '~/shared/hooks';
import { debounce, findScrollContainer } from '~/shared/utils';
import { StyledSticky, StyledStickyContent } from './styled';

type StickyProps = {
    /**
     * The distance to the top when sticky
     */
    gutterTop?: number;
    /**
     * The distance to the bottom when sticky
     */
    gutterBottom?: number;
    /**
     * The child Element
     */
    children: JSX.Element;
} & ComponentProps<typeof StyledSticky>;

let scrollContainerEl: HTMLElement | void;

export const Sticky = memo(
    ({
        gutterTop: gutterTopProp,
        gutterBottom: gutterBottomProp,
        children: child,
        className,
        as,
    }: StickyProps) => {
        const { traits } = useTheme();
        const gutterTop = gutterTopProp || traits.sticky.gutterTop;
        const gutterBottom = gutterBottomProp || traits.sticky.gutterBottom;
        const stickyRef = useRef<HTMLDivElement>(null);
        const bufferRef = useRef<HTMLDivElement>(null);
        const [stickyStyle, setStickyStyle] = useState<CSSProperties>({ top: gutterTop });
        const [bufferStyle, setBufferStyle] = useState<CSSProperties>({});
        const scrollDirection = useScrollDirection();

        if (Children.count(child) !== 1) {
            throw Error('Sticky must have exactly 1 child');
        }

        const handleHeightChange = useCallback(
            ({
                stickyElement,
                valueTop,
                valueBottom,
                overflowPx,
            }: {
                stickyElement: HTMLElement;
                valueTop: number;
                valueBottom: number;
                overflowPx: number;
            }) => {
                // If height has changed, calculate the whitespace that occured between the window and the sticky section
                const parentBottom =
                    stickyElement.parentElement?.getBoundingClientRect().bottom || 0;
                const elementBottom = stickyElement.getBoundingClientRect().bottom;
                const pixelsFromParentBottom = elementBottom - parentBottom;

                let distanceFromViewport = 0;

                // First check if sticky section is at the bottom of parent.
                if (pixelsFromParentBottom >= 0) {
                    return false;
                }

                // Check if sticky section is above viewport (gutter taken into account)
                const distanceFromScrollContainerBottom =
                    (scrollContainerEl?.clientHeight || 0) - (elementBottom + gutterBottom);
                distanceFromViewport = Math.max(distanceFromScrollContainerBottom, 0);

                // if there is a distance, set properties that drags the sticky section down to fill the gap
                if (!distanceFromViewport) {
                    return false;
                }

                if (scrollDirection === 'up') {
                    setBufferStyle({
                        height: stickyElement.offsetTop + distanceFromViewport,
                    });
                    // Set sticky bottom to the current distance to viewport, and transitionDuration to 0, so we can animate from that point.
                    setStickyStyle({
                        bottom: `${distanceFromViewport}px`,
                        transitionDuration: '0s',
                    });
                    requestAnimationFrame(() => {
                        setStickyStyle({
                            bottom: `${valueBottom}px`,
                            transitionDuration: '',
                        });
                    });
                } else {
                    // Set sticky top to the current overflow and distance to viewport, and transitionDuration to 0, so we can animate from that point.
                    setStickyStyle({
                        top: `${(overflowPx + distanceFromViewport) * -1}px`,
                        transitionDuration: '0s',
                    });
                    requestAnimationFrame(() => {
                        setStickyStyle({
                            top: `${valueTop}px`,
                            transitionDuration: '',
                        });
                    });
                }
                return true;
            },
            [gutterBottom, scrollDirection]
        );

        const resetSticky = useCallback(
            (stickyElement: HTMLElement) => {
                // If top is equal to gutterTop, we don't have to do anything
                if (stickyStyle.top === gutterTop) {
                    return;
                }
                // reset styles and have regular sticky functionality, and transition nicely from current position to default
                setStickyStyle({
                    top: `${stickyElement.getBoundingClientRect().top}px`,
                    transitionDuration: '0s',
                });
                requestAnimationFrame(() => {
                    setBufferStyle({ height: '' });
                    setStickyStyle({ top: gutterTop, transitionDuration: '' });
                });
            },
            [gutterTop, stickyStyle.top]
        );

        const setStickyProperties = useCallback(
            (isHeightChange = true) => {
                if (!scrollContainerEl) {
                    scrollContainerEl = findScrollContainer(stickyRef.current || undefined);
                }
                if (!(stickyRef.current && bufferRef.current && scrollContainerEl)) {
                    return;
                }

                const overflowPx = stickyRef.current.scrollHeight - scrollContainerEl.clientHeight;

                // Calculate if we still overflow, when subtracting gutters
                const isOverflowing = overflowPx + gutterTop + gutterBottom > 0;
                if (!isOverflowing) {
                    resetSticky(stickyRef.current);
                    return;
                }

                // We find the values by adding the overflow with the opposite gutter.
                // Example when scrolling down, we want to set a negative 'top' value on the stickyElement, so the bottom is visible
                // heightWindow = hw = 1000
                // heightSticky = hs = 1040
                // gutterTop = gt = 60
                // gutterBottom = gb = 40
                // overflow = o = hs - hw
                // topValue = (o + gb) * -1 = (40 + 40) * -1 = -80px
                // So in this scenario, if we set a sticky value of -80px on top, the sticky element will be dragged -80px upwards
                // Which means the bottom of the sticky element, is placed 40px from the bottom of th screen (the gutterBottom value)
                // Sticky distance from bottom = hw - hs - topValue = 1000 - 1040 - -80 = 40
                //
                // It also works when overflow is negative, but because of gutters, needs to be handled
                // hw = 1120
                // topValue = (o + gb) * -1 = (-80 + 40) * -1 = 40px
                // Sticky distance from bottom = hw - hs - topValue = 1120 - 1040 -40 = 40
                const valueTop = (gutterBottom + overflowPx) * -1;
                const valueBottom = (gutterTop + overflowPx) * -1;
                // Return if handling of height change has set sticky
                const hasSetSticky =
                    isHeightChange &&
                    handleHeightChange({
                        stickyElement: stickyRef.current,
                        valueTop,
                        valueBottom,
                        overflowPx,
                    });
                if (hasSetSticky) {
                    return;
                }

                // If still overflowing, but height hasn't changed,
                // set default sticky properties based on scrollDirection
                setBufferStyle({ height: stickyRef.current.offsetTop });
                if (scrollDirection === 'up') {
                    setStickyStyle({ bottom: `${valueBottom}px` });
                } else {
                    setStickyStyle({ top: `${valueTop}px` });
                }
            },
            [scrollDirection, gutterTop, gutterBottom, handleHeightChange, resetSticky]
        );

        useEffect(() => {
            setStickyProperties(false);
        }, [scrollDirection, setStickyProperties]);

        const handleClick = useCallback(() => {
            setStickyProperties(false);
        }, [setStickyProperties]);

        useEffect(() => {
            if (!stickyRef.current) {
                return;
            }
            const handleResizeDebounced = debounce(setStickyProperties, 50);
            let resizes = 0;
            const stickyEl = stickyRef.current;
            const observer = new window.ResizeObserver((entries) => {
                resizes += 1;
                if (entries[0] && resizes > 1) {
                    handleResizeDebounced();
                }
            });
            // Observe to detect height changes
            observer.observe(stickyEl);
            // Add a listener to click to handle scenarios where the sticky section shrinks towards bottom when content changes
            stickyEl.addEventListener('click', handleClick);
            return () => {
                observer.disconnect();
                stickyEl.removeEventListener('click', handleClick);
            };
        }, [setStickyProperties, handleClick]);

        return (
            <StyledSticky as={as} className={className}>
                <div aria-hidden="true" ref={bufferRef} style={bufferStyle} />
                <StyledStickyContent
                    as={child.type}
                    ref={stickyRef}
                    style={stickyStyle}
                    {...child.props}
                />
            </StyledSticky>
        );
    }
);
